import type { PersonProfileFields } from '@/lib/person-profile';
/**
 * Plugin-app prospect data: the same endpoints the extension consumes today
 * (`GET /prospects?profile_id=` and `GET /enrichment/task/{id}/status`),
 * typed minimally — the panel card renders identity + contact rows and
 * passes everything else through untouched.
 */
import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '@/store/base-query';

export interface PluginDate {
  day?: number | null;
  month?: number | null;
  year?: number | null;
}

export interface PluginExperience {
  is_current?: boolean | null;
  is_default?: boolean | null;
  description?: string | null;
  title?: string | null;
  company?: string | null;
  company_linkedin_profile_url?: string | null;
  logo_url?: string | null;
  starts_at?: PluginDate | null;
  ends_at?: PluginDate | null;
  location?: string | null;
}

export interface PluginEducation {
  alma_mater?: string | null;
  institute_name?: string | null;
  logo_url?: string | null;
  institute_logo_url?: string | null;
  alma_mater_linkedin_profile_url?: string | null;
  institute_linkedin_url?: string | null;
  school?: string | null;
  degree_name?: string | null;
  field_of_study?: string | null;
  starts_at?: PluginDate | null;
  ends_at?: PluginDate | null;
  start_date?: string | number | PluginDate | null;
  end_date?: string | number | PluginDate | null;
}

export interface PluginProspect extends PersonProfileFields {
  id: string | null;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  headline?: string | null;
  occupation?: string | null;
  summary?: string | null;
  profile_pic_url?: string | null;
  public_linkedin_url?: string | null;
  public_identifier?: string | null;
  current_company_id?: string | null;
  /** Pre-joined display location ("Toronto, Ontario, Canada") when present. */
  location?: string | null;
  city?: string | null;
  state?: string | null;
  /** The NEW-API parser writes `country`; older/Wiza rows carry
   * `country_full_name`. Read both — see locationOf(). */
  country?: string | null;
  country_full_name?: string | null;
  connections?: number | null;
  skills?: string[] | null;
  languages?: string[] | null;
  experiences?: PluginExperience[] | null;
  education?: PluginEducation[] | null;
  [key: string]: unknown;
}

export interface PluginLead {
  id: string;
  primary_email?: string | null;
  primary_email_status?: 'reliable' | 'unreliable' | 'unknown' | null;
  other_emails?: string[] | null;
  personal_emails?: string[] | null;
  /** Per-field "a lookup actually ran" timestamps from the enrichment
   *  activities. ``personal_email`` present with no ``personal_emails`` is a
   *  confirmed miss, not a pending run — the difference the panel needs to
   *  stop waiting and say so. */
  contact_attempts?: Record<string, string | null> | null;
  personal_phone_numbers?: string[] | null;
  phone_metadata?: Record<string, Record<string, unknown>> | null;
  primary_domain?: string | null;
  [key: string]: unknown;
}

export interface PluginCompany {
  id?: string;
  name?: string | null;
  domain?: string | null;
  website?: string | null;
  logo_url?: string | null;
  profile_pic_url?: string | null;
  description?: string | null;
  tagline?: string | null;
  industry?: string | null;
  headcount?: number | null;
  employee_count_on_linkedin?: number | null;
  employee_count_min?: number | null;
  employee_count_max?: number | null;
  founded_year?: number | null;
  universal_name_id?: string | null;
  hq?: { city?: string; state?: string; country?: string } | null;
  [key: string]: unknown;
}

export interface PluginTimeline {
  enrichment_type?: 'partial' | 'full';
  status?: 'pending' | 'completed' | 'failed';
  completed_at?: string;
  enriched_provider?: string;
  timeline?: {
    provider: string;
    result?: { emails?: string[]; phones?: string[] };
  }[];
}

export interface PluginProspectResponse {
  prospect: PluginProspect | null;
  lead: PluginLead | null;
  enrichment_timeline: PluginTimeline | null;
  company: PluginCompany | null;
}

export interface PluginCrmAccount {
  crm: string;
  record_id: string;
  name?: string | null;
  /** Null when the account has no website on the record. */
  domain?: string | null;
}

/** Mirrors ManualEnrichmentRequest. */
export interface PluginManualEnrichBody {
  enrichment_type: 'partial' | 'phone_only' | 'full';
  lead_list_id: string;
  linkedin_url?: string;
  first_name?: string;
  last_name?: string;
  /** The backend tolerates a plain company name here when no domain exists. */
  company_domain?: string;
  /** Explicit paid work-email attempt, rather than result-based billing. */
  search_email: boolean;
  /** One key per click, so a retried send cannot double-charge. */
  idempotency_key?: string;
  current_email?: string;
  current_email_status?: string;
  /** The record being enriched; the workflow writes the result back to it. */
  crm_record?: { crm: string; object_type: string; record_id: string };
}

export interface PluginEnrichTaskStarted {
  task_id: string;
  status: string;
  lead_id?: string | null;
}

export interface PluginReverseEmailResult {
  /** imported | already_in_list | no_match */
  status: string;
  lead_id?: string | null;
  linkedin_url?: string | null;
}

export interface PluginCrmPushResult {
  success: boolean;
  /** done | skipped | failed */
  outcome: string;
  reason?: string | null;
  error?: string | null;
  matched_viewed_record?: boolean | null;
}

/** One CRM person record, read server-side through the org's connection. */
export interface PluginCrmRecord {
  crm: string;
  object_type: string;
  record_id: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  title?: string | null;
  company_name?: string | null;
  company_domain?: string | null;
  /** Present only when the CRM itself holds a LinkedIn URL for the person. */
  linkedin_url?: string | null;
  /** linkedin | name_company | reverse_email | insufficient. */
  suggested_path: string;
  /** The caller's private enrichments list; every enrich call needs it. */
  lead_list_id: string;
  email_status?: string | null;
  /** The server judged the record's email good enough to reuse. */
  email_is_accepted?: boolean;
}

export interface PluginUser {
  user_id: string;
  first_name?: string | null;
  last_name?: string | null;
  image_url?: string | null;
  email: string;
  organization_id: string;
  organization_name?: string | null;
}

export interface PluginCredits {
  /** Single organisation balance — the number the header shows. */
  count: number;
  email_credits: number;
  phone_credits: number;
  last_updated?: string;
}

export interface PluginTaskStatus {
  task_id?: string;
  status?: string;
  error?: string | null;
  /** no_data | provider_error | timeout — present once the BE branch lands. */
  failure_class?: string | null;
  /** Why the task failed, e.g. person_opted_out (refused, unbilled). */
  failure_kind?: string | null;
  [key: string]: unknown;
}

export const pluginProspectApi = createApi({
  reducerPath: 'pluginProspectApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['PluginProspect'],
  endpoints: (builder) => ({
    getProspectByProfileId: builder.query<PluginProspectResponse, string>({
      query: (profileId) =>
        `/prospects?profile_id=${encodeURIComponent(profileId)}`,
      providesTags: (_, __, profileId) => [
        { type: 'PluginProspect', id: profileId },
      ],
      // The panel REMOUNTS on every page change (app.component keys the view
      // on the URL), so this retention is what "going back to a profile" costs:
      // at 30s, stepping away for half a minute made the panel refetch from
      // scratch and look like it reloaded (Lev 2026-08-17). 5 minutes keeps a
      // revisit instant.
      //
      // Safe for the old 404-for-uninitialized concern: a cached miss still
      // satisfies `needsInit` in prospect-view, which re-fires the (idempotent)
      // init command on mount, and the `prospect_initialization` event
      // invalidates this tag — so a completed init is never hidden behind the
      // cached rejection.
      keepUnusedDataFor: 300,
    }),
    /** Salesforce account → its company domain.
     *
     * A Salesforce account URL carries only the record id, so the domain has
     * to come from the record. Reading it server-side (rather than from the
     * page) keeps the panel working without touching the customer's CRM DOM. */
    getCrmAccount: builder.query<
      PluginCrmAccount,
      string | { crm: 'hubspot'; recordId: string; portalId: string }
    >({
      query: (arg) => {
        const crm = typeof arg === 'string' ? 'salesforce' : arg.crm;
        const recordId = typeof arg === 'string' ? arg : arg.recordId;
        const portal =
          typeof arg === 'string'
            ? ''
            : `&portal_id=${encodeURIComponent(arg.portalId)}`;
        return `/crm/records/account?crm=${crm}&record_id=${encodeURIComponent(recordId)}${portal}`;
      },
      keepUnusedDataFor: 300,
    }),
    /** Billable enrich steps, shared with the injected button's flow. */
    manualEnrich: builder.mutation<
      PluginEnrichTaskStarted,
      PluginManualEnrichBody
    >({
      query: (body) => ({ url: '/enrichment/manual', method: 'POST', body }),
    }),
    /** Costs 2 credits per attempt, charged whether or not it matches. */
    reverseEmailSingle: builder.mutation<
      PluginReverseEmailResult,
      { email: string; lead_list_id: string }
    >({
      query: (body) => ({
        url: '/enrichment/reverse-email-single',
        method: 'POST',
        body,
      }),
    }),
    /** Salesforce contact or lead identity fields. Free. */
    getCrmRecord: builder.query<
      PluginCrmRecord,
      {
        object: string;
        recordId: string;
        crm?: 'salesforce' | 'hubspot';
        portalId?: string;
      }
    >({
      query: ({ object, recordId, crm = 'salesforce', portalId }) =>
        `/crm/records/read?crm=${crm}&object=${encodeURIComponent(object)}&record_id=${encodeURIComponent(recordId)}${portalId ? `&portal_id=${encodeURIComponent(portalId)}` : ''}`,
      keepUnusedDataFor: 300,
    }),
    getTaskStatus: builder.query<PluginTaskStatus, string>({
      query: (taskId) =>
        `/enrichment/task/${encodeURIComponent(taskId)}/status`,
      keepUnusedDataFor: 0,
    }),
    getCredits: builder.query<PluginCredits, void>({
      query: () => '/extension/credits',
      keepUnusedDataFor: 60,
    }),
    getUserMetadata: builder.query<PluginUser, void>({
      query: () => '/extension/user-metadata',
      keepUnusedDataFor: 600,
    }),
  }),
});

export const {
  useGetProspectByProfileIdQuery,
  useGetCrmAccountQuery,
  useGetCrmRecordQuery,
  useManualEnrichMutation,
  useReverseEmailSingleMutation,
  useLazyGetTaskStatusQuery,
  useGetCreditsQuery,
  useGetUserMetadataQuery,
  util: pluginProspectApiUtil,
} = pluginProspectApi;
